import * as React from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  Search,
  MessagesSquare,
  Loader2,
  Inbox,
  Link2,
  FileText,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { Interview, InterviewStatus } from "@mycompbox/shared";
import {
  useInterviews,
  useInterviewTranscript,
} from "@/hooks/queries/use-interviews";
import type { InterviewTranscript } from "@/lib/api/interviews";
import { RunOnMachineDialog } from "./run-on-machine-dialog";
import { useIdeaHref } from "@/hooks/use-new-look";

/**
 * Interview transcript history + reuse UI (ENG-145).
 *
 * Lists the caller's persisted interviews (via {@link useInterviews}), lets them
 * filter by lifecycle status and free-text search, drill into a read-only
 * transcript ({@link useInterviewTranscript} — a durable snapshot for
 * completed/archived interviews), and "start a new request from" a past
 * transcript. The seed reuses {@link RunOnMachineDialog}, so the prior
 * transcript rides along as reference context for a FRESH machine-driven
 * interview — never an auto-generated EpicRequest, and (thanks to that dialog's
 * resume detection) never a duplicate of an in-progress interview.
 *
 * This is presentational + start-a-new-interview only. It reuses the ENG-144
 * interview client/hooks wholesale and adds no API surface.
 */

const STATUS_LABELS: Record<InterviewStatus, string> = {
  in_progress: "In progress",
  completed: "Completed",
  archived: "Archived",
};

const STATUS_BADGE: Record<InterviewStatus, string> = {
  in_progress: "bg-purple-900/30 text-purple-300",
  completed: "bg-green-900/30 text-green-300",
  archived: "bg-white/[0.06] text-muted-foreground",
};

/** "all" plus the concrete statuses, for the filter select. */
type StatusFilter = "all" | InterviewStatus;

/**
 * A completed/archived interview holds a DURABLE transcript snapshot, so it can
 * be reopened as reference for a new interview. In-progress ones cannot (there's
 * nothing final to seed from, and the user should resume them instead).
 */
function isReusable(status: InterviewStatus): boolean {
  return status === "completed" || status === "archived";
}

/** A stable, searchable string for a row (id, linked ER, status label). */
function searchableText(interview: Interview): string {
  return [
    interview.id,
    interview.epicRequestId ?? "",
    STATUS_LABELS[interview.status],
    interview.status,
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Flattens a transcript into reference text for the seed payload. Capped so a
 * long interview doesn't bloat the job payload; the daemon only needs the gist
 * as reference, not a verbatim replay.
 */
const SEED_REFERENCE_MAX_CHARS = 4000;
function buildSeedReference(transcript: InterviewTranscript): string {
  const text = transcript.turns
    .map((turn) => {
      const who = turn.role === "user" ? "You" : "Formulator";
      return `${who}: ${turn.content}`;
    })
    .join("\n");
  return text.length > SEED_REFERENCE_MAX_CHARS
    ? `${text.slice(0, SEED_REFERENCE_MAX_CHARS)}…`
    : text;
}

// =============================================================================
// Transcript detail dialog
// =============================================================================

interface TranscriptDialogProps {
  interview: Interview | null;
  onClose: () => void;
}

/**
 * Read-only transcript view for a selected interview. For completed/archived
 * interviews the server returns the durable snapshot; the seed action is offered
 * only for those (an in-progress interview should be resumed, not seeded from).
 */
function TranscriptDialog({ interview, onClose }: TranscriptDialogProps) {
  const open = interview !== null;
  const { data: transcript, isLoading } = useInterviewTranscript(
    interview?.id,
    { enabled: open }
  );

  const reusable = interview ? isReusable(interview.status) : false;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessagesSquare className="h-4 w-4" />
            Interview transcript
          </DialogTitle>
          <DialogDescription>
            {interview
              ? `${STATUS_LABELS[interview.status]} · started ${formatDistanceToNow(
                  new Date(interview.createdAt),
                  { addSuffix: true }
                )}`
              : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-white/[0.06] p-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading transcript…
            </div>
          ) : !transcript || transcript.turns.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              This interview has no recorded transcript yet.
            </p>
          ) : (
            <div className="space-y-3">
              {transcript.source === "snapshot" && (
                <Badge className="bg-white/[0.06] text-xs text-muted-foreground">
                  <FileText className="h-3 w-3 mr-1" />
                  Saved snapshot
                </Badge>
              )}
              {transcript.turns.map((turn) => (
                <div
                  key={turn.id}
                  className={cn(
                    "rounded-md p-2 text-sm",
                    turn.role === "user"
                      ? "bg-blue-900/[0.08] border border-blue-800/20"
                      : "bg-white/[0.03] border border-white/[0.06]"
                  )}
                >
                  <div className="mb-1 text-xs font-medium text-muted-foreground">
                    {turn.role === "user" ? "You" : "Formulator"}
                  </div>
                  <div className="whitespace-pre-wrap break-words">
                    {turn.content}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {/* Seed action (ENG-145): only for reusable (completed/archived)
              transcripts, and only once the snapshot is loaded so we have
              reference content to carry. */}
          {reusable && interview && transcript && transcript.turns.length > 0 && (
            <RunOnMachineDialog
              seedFromInterview={{
                interviewId: interview.id,
                reference: buildSeedReference(transcript),
              }}
            >
              Start a new request from this
            </RunOnMachineDialog>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// History list row
// =============================================================================

interface HistoryRowProps {
  interview: Interview;
  onSelect: (interview: Interview) => void;
}

function HistoryRow({ interview, onSelect }: HistoryRowProps) {
  const ideaHref = useIdeaHref();
  return (
    <button
      type="button"
      onClick={() => { onSelect(interview); }}
      className="w-full text-left rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 transition-colors hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <MessagesSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="font-medium truncate">
            Interview {interview.id.slice(0, 8)}
          </span>
        </div>
        <Badge
          className={cn("text-xs shrink-0", STATUS_BADGE[interview.status])}
        >
          {STATUS_LABELS[interview.status]}
        </Badge>
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
        <span>
          Started{" "}
          {formatDistanceToNow(new Date(interview.createdAt), {
            addSuffix: true,
          })}
        </span>
        {interview.epicRequestId && (
          <Link
            to={ideaHref(interview.epicRequestId)}
            onClick={(e) => { e.stopPropagation(); }}
            className="inline-flex items-center gap-1 text-blue-300 hover:underline"
          >
            <Link2 className="h-3 w-3" />
            View request
          </Link>
        )}
      </div>
    </button>
  );
}

// =============================================================================
// History list
// =============================================================================

/**
 * The transcript-history view. Hosted as a tab on the epic-requests page.
 */
export function InterviewHistory() {
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selected, setSelected] = React.useState<Interview | null>(null);

  // Status is filtered server-side (the API supports it); free text is filtered
  // client-side over the returned page (interviews carry no title to search).
  const { data, isLoading } = useInterviews(
    statusFilter === "all" ? {} : { status: statusFilter }
  );

  const interviews = data?.data ?? [];

  const filtered = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return interviews;
    return interviews.filter((interview) =>
      searchableText(interview).includes(q)
    );
  }, [interviews, searchQuery]);

  return (
    <div>
      {/* Filters */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center gap-2">
        <Select
          value={statusFilter}
          onValueChange={(value) => { setStatusFilter(value as StatusFilter); }}
        >
          <SelectTrigger className="w-full md:w-[200px]" aria-label="Filter by status">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>

        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search transcripts…"
            aria-label="Search transcripts"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); }}
            className="pl-9"
          />
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Inbox className="mx-auto h-8 w-8 text-muted-foreground/60 mb-3" />
          <p className="text-muted-foreground">
            {searchQuery || statusFilter !== "all"
              ? "No transcripts match your filter."
              : "No interviews yet. Start one with “Run on Your Machine.”"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((interview) => (
            <HistoryRow
              key={interview.id}
              interview={interview}
              onSelect={setSelected}
            />
          ))}
        </div>
      )}

      <TranscriptDialog
        interview={selected}
        onClose={() => { setSelected(null); }}
      />
    </div>
  );
}
